import React, { useMemo } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// material-ui
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

// project import
import MainCard from "components/MainCard";
import Tooltip from "components/@extended/Tooltip";

// assets
import { WarningFilled } from "@ant-design/icons";

// constants
const DEDUCTION_DAY = 1;

dayjs.extend(utc);
dayjs.extend(timezone);

const UsageForecastBlock = ({ settingsData, dashboardData }) => {
  const theme = useTheme();

  const timezone = settingsData?.timeConfig?.timezone || "UTC";
  const today = useMemo(() => dayjs().tz(timezone), [timezone]);
  const data = useMemo(() => {
    if (!dashboardData) return null;
    const remainingDays = dashboardData.remainingDays || 0;
    const totalDays = 365;

    const thresholds = [
      {
        label: "Normal",
        color: "#69B060",
        days: remainingDays - 365,
        depletionText: "> 365 days to depletion",
        isApprox: true,
      },
      {
        label: "Predictive Alert",
        color: "#F2B661",
        days: remainingDays - 90,
        depletionText: "365 -> 91 days to depletion",
      },
      {
        label: "Critical Alert",
        color: "#F39E4E",
        days: remainingDays - 30,
        depletionText: "90 -> 30 days to depletion",
      },
      {
        label: "Low Balance",
        color: "#E75E4D",
        days: remainingDays,
        depletionText: "29 -> 0 days to depletion",
      },
      {
        label: "Grace Period",
        color: "#7B2821",
        days: remainingDays + 3,
        depletionText: "Day +1 / +2 / +3 retries",
      },
      {
        label: "Service Suspended",
        color: "#4A5A65",
        days: totalDays,
        depletionText: "Credits depleted",
        hideDays: true,
      },
    ];

    const segments = [];
    const transitionMarkers = [];
    let currentStart = 0;

    thresholds.forEach((t) => {
      const end = Math.min(totalDays, Math.max(currentStart, t.days));
      if (end > currentStart) {
        const segmentDays = Math.floor(end - currentStart);
        segments.push({
          label: t.label,
          color: t.color,
          percentage: ((end - currentStart) / totalDays) * 100,
          startDay: currentStart,
          endDay: end,
          tooltipTitle: t.label,
          tooltipDates: `${today.add(currentStart, "day").format("YYYY-MM-DD")} -> ${today.add(end, "day").format("YYYY-MM-DD")}`,
          tooltipDetails: t.hideDays
            ? t.depletionText
            : `${t.isApprox ? "~" : ""}${segmentDays} days · ${t.depletionText}`,
        });

        // Add transition date marker if it's not the start or end of the bar
        if (end > 0 && end < totalDays) {
          const isDepletion = t.label === "Low Balance";
          const isPostDepletion = currentStart >= remainingDays;
          transitionMarkers.push({
            percentage: (end / totalDays) * 100,
            date: today.add(end, "day").format("MM-DD"),
            isDepletion,
            isPostDepletion,
          });
        }
        currentStart = end;
      }
    });

    // Produce monthly deduction markers
    const monthlyMarkers = [];

    const nextDeductionAt = Number(dashboardData.nextDeductionAt);
    let currentDeduction = Number.isFinite(nextDeductionAt)
      ? dayjs.unix(nextDeductionAt).tz(timezone).startOf("day")
      : today.date(DEDUCTION_DAY).startOf("day");
    // Ignore stale API dates and fall back to the next billing day.
    if (currentDeduction.isBefore(today.startOf("day"))) {
      currentDeduction = today.date(DEDUCTION_DAY).startOf("day");
      if (currentDeduction.isBefore(today.startOf("day"))) {
        currentDeduction = currentDeduction.add(1, "month");
      }
    }

    for (let i = 0; i < 12; i++) {
      const diffDays = currentDeduction.diff(today, "day");
      if (diffDays < 0 || diffDays > totalDays) break;

      monthlyMarkers.push({
        percentage: (diffDays / totalDays) * 100,
        daysFromToday: diffDays,
        date: currentDeduction.format("MM-DD"),
        fullDate: currentDeduction.format("YYYY-MM-DD"),
      });
      currentDeduction = currentDeduction.add(1, "month");
    }

    return {
      startDate: today.format("YYYY-MM-DD"),
      startDateShort: today.format("MM-DD"),
      endDateShort: today.add(totalDays, "day").format("MM-DD"),
      segments,
      transitionMarkers,
      monthlyMarkers,
    };
  }, [dashboardData, today]);

  if (!data) return null;

  const legendItems = [
    { label: "Normal", color: "#69B060" },
    { label: "Predictive Alert", color: "#F2B661" },
    { label: "Critical Alert", color: "#F39E4E" },
    { label: "Low Balance", color: "#E75E4D" },
    { label: "Grace Period", color: "#7B2821" },
    { label: "Service Suspended", color: "#4A5A65" },
  ];
  const monthlyDeduction = Number(
    dashboardData?.monthlyDeduction ?? dashboardData?.nextDeduction ?? 0,
  );
  const deductionAmount = Number.isFinite(monthlyDeduction)
    ? monthlyDeduction
    : 0;
  const currentBalance = Number(dashboardData?.balance || 0);

  const getSegmentDeductions = (segment) => {
    const deductionsInSegment = data.monthlyMarkers.filter(
      (marker) =>
        marker.daysFromToday > segment.startDay &&
        marker.daysFromToday <= segment.endDay,
    ).length;
    const deductionsThroughSegment = data.monthlyMarkers.filter(
      (marker) => marker.daysFromToday <= segment.endDay,
    ).length;

    return {
      deduction: deductionsInSegment * deductionAmount,
      remaining: Math.max(
        0,
        currentBalance - deductionsThroughSegment * deductionAmount,
      ),
    };
  };

  return (
    <MainCard
      sx={{ border: "none", boxShadow: "none", bgcolor: "transparent" }}
      contentSX={{ p: 0 }}
    >
      <Stack spacing={3}>
        <Stack spacing={0.5}>
          <Typography
            sx={{
              color: "#172664",
              fontSize: "15px",
              fontWeight: "bold",
            }}
          >
            Usage Forecast
          </Typography>
          <Typography sx={{ fontSize: "12px", color: "rgba(0, 0, 0, 0.45)" }}>
            Hover any segment for details
          </Typography>
        </Stack>

        <Box sx={{ position: "relative", pt: 8, pb: 12 }}>
          {/* Vertical "Today" line with dot */}
          <Box
            sx={{
              position: "absolute",
              top: 5,
              left: "50px",
              width: "1px",
              height: "95px",
              bgcolor: "#172664",
              zIndex: 10,
              pointerEvents: "none",
            }}
          >
            {/* Top Dot */}
            <Box
              sx={{
                width: 4,
                height: 4,
                bgcolor: "#172664",
                borderRadius: "50%",
                position: "absolute",
                top: 0,
                left: -1.5,
              }}
            />
          </Box>

          {/* "Today" text ABOVE the bar */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: "50px",
              ml: 1,
              pointerEvents: "none",
            }}
          >
            <Stack spacing={0} alignItems='flex-start'>
              <Typography
                variant='caption'
                sx={{ fontWeight: "bold", color: "#172664", fontSize: "12px" }}
              >
                Today
              </Typography>
              <Typography
                variant='caption'
                sx={{
                  color: "rgba(0, 0, 0, 0.45)",
                  fontSize: "11px",
                  mt: -0.5,
                }}
              >
                {data.startDate}
              </Typography>
            </Stack>
          </Box>

          {/* Monthly Deduction markers ABOVE the bar */}
          <Box
            sx={{
              position: "absolute",
              top: 40,
              left: "50px",
              width: "calc(100% - 100px)",
              pointerEvents: "default",
            }}
          >
            {data.monthlyMarkers.map((marker, index) => {
              const deductionAmount = Number(
                dashboardData?.monthlyDeduction ??
                  dashboardData?.nextDeduction ??
                  0,
              );
              const monthlyDeduction = Number.isFinite(deductionAmount)
                ? deductionAmount
                : 0;
              const remainingCredits = Math.max(
                0,
                (dashboardData?.balance ?? 0) - (index + 1) * monthlyDeduction,
              );

              return (
                <Box
                  key={index}
                  sx={{
                    position: "absolute",
                    left: `${marker.percentage}%`,
                    transform: "translateX(-50%)",
                  }}
                >
                  <Tooltip
                    title={
                      <Stack spacing={0.5} sx={{ p: 0.5 }}>
                        <Typography
                          variant='caption'
                          color='inherit'
                          sx={{ fontWeight: 600 }}
                        >
                          {marker.fullDate}
                        </Typography>
                        <Typography variant='caption' color='inherit'>
                          Deduction: -{monthlyDeduction.toLocaleString()} Credits
                        </Typography>
                        <Typography variant='caption' color='inherit'>
                          Remaining: {remainingCredits.toLocaleString()} Credits
                        </Typography>
                      </Stack>
                    }
                    placement='top'
                    color='#737373'
                  >
                    <Typography
                      variant='caption'
                      sx={{
                        color: "#172664",
                        fontWeight: "bold",
                        fontSize: "10px",
                        cursor: "pointer",
                      }}
                    >
                      ▾
                    </Typography>
                  </Tooltip>
                </Box>
              );
            })}
            {data.transitionMarkers
              .filter((m) => m.isDepletion)
              .map((marker, index) => (
                <Box
                  key={`dep-${index}`}
                  sx={{
                    position: "absolute",
                    left: `${marker.percentage}%`,
                    transform: "translateX(-50%)",
                  }}
                >
                  <WarningFilled
                    style={{
                      color: "#DB0046",
                      fontSize: "16px",
                      marginBottom: "-4px",
                    }}
                  />
                </Box>
              ))}
          </Box>

          {/* Forecast Bar */}
          <Box
            sx={{
              height: 28,
              width: "calc(100% - 100px)",
              mx: "50px",
              display: "flex",
              overflow: "hidden",
              mt: 2.5,
              position: "relative",
              borderRadius: "14px",
              border: "none",
              zIndex: 5,
            }}
          >
            {data.segments.map((segment, index) => (
              <Box
                key={index}
                sx={{
                  width: `${segment.percentage}%`,
                  height: "100%",
                  position: "relative",
                  "& > div": { width: "100%", height: "100%" },
                }}
              >
                <Tooltip
                  title={
                    <Stack spacing={0.5} sx={{ p: 0.5 }}>
                      <Typography variant='caption' color='inherit'>
                        {today.add(segment.endDay, "day").format("YYYY-MM-DD")}
                      </Typography>
                      <Typography variant='caption' color='inherit'>
                        Deduction: {getSegmentDeductions(segment).deduction > 0 ? "-" : ""}
                        {getSegmentDeductions(segment).deduction.toLocaleString()} Credits
                      </Typography>
                      <Typography variant='caption' color='inherit'>
                        Remaining: {getSegmentDeductions(segment).remaining.toLocaleString()} Credits
                      </Typography>
                    </Stack>
                  }
                  placement='top'
                  color='#737373'
                >
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      bgcolor: segment.color,
                      cursor: "pointer",
                      transition: "opacity 0.2s",
                      "&:hover": { opacity: 0.8 },
                    }}
                  />
                </Tooltip>
              </Box>
            ))}
          </Box>

          {/* Dates BELOW the bar */}
          <Box
            sx={{
              position: "relative",
              width: "calc(100% - 100px)",
              mx: "50px",
              mt: 1,
            }}
          >
            {/* Start Date */}
            <Box
              sx={{
                position: "absolute",
                left: 0,
                transform: "translate(-50%, 10px)",
              }}
            >
              <Typography
                variant='caption'
                sx={{ color: "rgba(0, 0, 0, 0.45)", fontSize: "12px" }}
              >
                {data.startDateShort}
              </Typography>
            </Box>
            {data.transitionMarkers.map((marker, index) => {
              let marginTop = 0;
              if (marker.isDepletion) marginTop = 3.5;
              else if (marker.isPostDepletion) marginTop = 8;

              return (
                <Box
                  key={index}
                  sx={{
                    position: "absolute",
                    left: `${marker.percentage}%`,
                    transform: "translateX(-50%)",
                    textAlign: "center",
                    mt: marginTop,
                    zIndex: marker.isDepletion ? 2 : 1,
                  }}
                >
                  <Typography
                    variant='caption'
                    display='block'
                    sx={{
                      color: marker.isDepletion
                        ? "#DB0046"
                        : "rgba(0, 0, 0, 0.45)",
                      fontWeight: marker.isDepletion ? "bold" : "normal",
                      fontSize: "12px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {marker.date}
                  </Typography>
                  {marker.isDepletion && (
                    <Typography
                      variant='caption'
                      sx={{
                        color: "#DB0046",
                        fontWeight: "bold",
                        display: "block",
                        lineHeight: 1,
                        fontSize: "11px",
                        mt: -0.5,
                      }}
                    >
                      depletion
                    </Typography>
                  )}
                </Box>
              );
            })}
            {/* End Date */}
            <Box
              sx={{
                position: "absolute",
                right: 0,
                transform: "translate(50%, 10px)",
              }}
            >
              <Typography
                variant='caption'
                sx={{ color: "rgba(0, 0, 0, 0.45)", fontSize: "12px" }}
              >
                {data.endDateShort}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Stack direction='row' spacing={3} sx={{ mt: 2 }} alignItems='center'>
          {legendItems.map((item, index) => (
            <Stack key={index} direction='row' spacing={1} alignItems='center'>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  bgcolor: item.color,
                  borderRadius: "2px",
                }}
              />
              <Typography
                variant='caption'
                sx={{ color: "rgba(0, 0, 0, 0.65)", fontSize: "12px" }}
              >
                {item.label}
              </Typography>
            </Stack>
          ))}
          <Box sx={{ flexGrow: 1 }} />
          <Stack direction='row' spacing={1} alignItems='center'>
            <Typography
              variant='caption'
              sx={{ color: "#172664", fontWeight: "bold", fontSize: "10px" }}
            >
              ▾
            </Typography>
            <Typography
              variant='caption'
              sx={{ color: "rgba(0, 0, 0, 0.65)", fontSize: "12px" }}
            >
              Monthly deduction
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </MainCard>
  );
};

export default UsageForecastBlock;
